import { apiClient, ApiResponse } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { synchronizeCarrierDetailsChangeHydration } from '@changeTracking/pages/carrierDetails/formChangeTracking';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast';
import { Playlist } from '@store/orders/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { InsuranceSchema } from '@views/carriers/carrierDetails/carrierDetailsTab/components/AddInsuranceModal';
import { cloneDeep } from 'lodash';

import {
  carrierDetailsStore,
  CarrierPaymentHoldsViewModelT,
  ClaimReleaseViewModel,
  Insurance,
  setCarrierDetailsStore,
} from './carrierDetails';
import { setCarrierLookupStore } from './carrierLookup/store';
import { CarrierLookup } from './carrierLookup/types';
import { LaneSearchGridApiResponse } from './laneSearch/types';
import {
  ApprovedCarriersApiResponse,
  CarrierEditHistoryItem,
  DataSource,
  EquipmentCategoryT,
  PreferredLaneT,
} from './types';

export const handleCarrierError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
    });
    handleToast(ToastType.Error, errorMessages.join(', '));
  } else {
    handleToast(ToastType.Error, toastMessage);
  }
};

export async function fetchPlaylistsCarrier(): Promise<Playlist[] | undefined> {
  try {
    const response = await apiClient.get('playlist');

    if ('value' in response) {
      return response.value as Playlist[];
    }

    throw new Error('Failed to fetch playlist');
  } catch (error: unknown) {
    logError('fetchPlaylists', error as Error);
    handleToast(ToastType.Error, JSON.stringify(error));
  }
}

export const fetchApprovedCarriers = async (
  payload: Record<string, unknown>,
) => {
  try {
    const response: V4ApiResponse<ApprovedCarriersApiResponse> =
      await apiClient.post('Carrier/getcarriers', payload);
    return response;
  } catch (error) {
    handleCarrierError(error, 'Failed to get carrier grid data');
  }
};

//endpoint for updating or inserting insurance
//[HttpPut("{carrierId:int}/upsertinsurance")]
export async function upsertInsurance(
  carrierId: number,
  payload: InsuranceSchema,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: ApiResponse<Insurance> = await apiClient.put(
      `Carrier/${carrierId}/upsertinsurance`,
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    const beforeInsuranceUpdate = cloneDeep(carrierDetailsStore.carrierDetails);
    if (payload.operationType === 'Insert') {
      setCarrierDetailsStore('carrierDetails', 'carrierInsurances', (state) => [
        response.value,
        ...state,
      ]);
      handleToast(ToastType.Success, 'Insurance added successfully');
    } else {
      setCarrierDetailsStore('carrierDetails', 'carrierInsurances', (state) =>
        state.map((insurance) =>
          insurance.id === response.value.id ? response.value : insurance,
        ),
      );
      handleToast(ToastType.Success, 'Insurance updated successfully');
    }
    if (beforeInsuranceUpdate && carrierDetailsStore.carrierDetails) {
      synchronizeCarrierDetailsChangeHydration(
        beforeInsuranceUpdate,
        carrierDetailsStore.carrierDetails,
      );
    }
    return response;
  } catch (error: unknown) {
    throw error;
  }
}

export async function fetchLookupCarriers(params: {
  mc?: number;
  dot?: number;
  IsCarrierPacket?: boolean;
}) {
  try {
    const response: { value: CarrierLookup } = await apiClient.get(
      'Carrier/Lookup',
      {
        params,
      },
    );
    if (Boolean(response.value)) {
      response.value.assessments.assessments.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      response.value.insights.sort((a, b) => a.title.localeCompare(b.title));
      setCarrierLookupStore('carrierLookup', response.value);
    }
    return response.value;
  } catch (error: unknown) {
    handleCarrierError(error, 'Failed to get carrier lookup data');
    throw error;
  }
}

export async function fetchCarrierEditHistory(id: string) {
  try {
    const response: V4ApiResponse<CarrierEditHistoryItem[]> =
      await apiClient.get(`Carrier/EditHistory/${id}`);
    return response.value;
  } catch (error: unknown) {
    const e = createError(
      error,
      'Failed to fetch carrier edit history. Please try again later.',
    );
    handleToast(ToastType.Error, e.message);
    throw e;
  }
}

export async function requestCarrierInfo(
  payload: {
    dot: string;
    email: string;
  },
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: ApiResponse<boolean> = await apiClient.post(
      'carrier/requestcarrierinfo',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return response;
  } catch (error: unknown) {
    if (Array.isArray(error)) {
      const errorMessages = error.map((v) => {
        if (typeof v === 'string') {
          return v;
        } else if (typeof v === 'object' && v !== null && 'message' in v) {
          return (v as { message: string }).message;
        }
        return 'Unspecified/unknown error';
      });
      handleToast(ToastType.Error, errorMessages.join(', '));
    }
  }
}

export const createLaneRow: PreferredLaneT = {
  originLane: '',
  originCity: '',
  originState: '',
  originPostal: '',
  originCountry: 'US',
  destinationLane: '',
  destinationCity: '',
  destinationState: '',
  destinationPostal: '',
  destinationCountry: 'US',
  originLatitude: 0,
  originLongitude: 0,
  destinationLatitude: 0,
  destinationLongitude: 0,
  carrierId: 0,
  id: -1,
  equipmentCategory: 0,
  source: DataSource.Internal,
  canDelete: true,
  createdDate: '',
  createdBy: 0,
  modifiedDate: '',
  modifiedBy: 0,
  historyContext: '',
};

const dummyPreferredLaneData: PreferredLaneT[] = [
  createLaneRow,
  {
    originLane: 'Chicago, Illinois US 60605',
    originCity: '',
    originState: '',
    originPostal: '',
    originCountry: 'US',
    destinationLane: 'Atlanta, Georgia US 30319',
    destinationCity: '',
    destinationState: '',
    destinationPostal: '',
    destinationCountry: '',
    originLatitude: 0,
    originLongitude: 0,
    destinationLatitude: 0,
    destinationLongitude: 0,
    carrierId: 0,
    id: 1,
    equipmentCategory: 13,
    source: DataSource.ATG,
    canDelete: false,
    createdDate: '',
    createdBy: 0,
    modifiedDate: '',
    modifiedBy: 0,
    historyContext: '',
  },
  {
    originLane: 'Miami, Florida US 33116',
    originCity: '',
    originState: '',
    originPostal: '',
    originCountry: 'US',
    destinationLane: 'Austin, Texas US 78716',
    destinationCity: '',
    destinationState: '',
    destinationPostal: '',
    destinationCountry: '',
    originLatitude: 0,
    originLongitude: 0,
    destinationLatitude: 0,
    destinationLongitude: 0,
    carrierId: 0,
    id: 2,
    equipmentCategory: 13,
    source: DataSource.ATG,
    canDelete: false,
    createdDate: '',
    createdBy: 0,
    modifiedDate: '',
    modifiedBy: 0,
    historyContext: '',
  },
  {
    originLane: 'Charlotte, North Carolina US 28203',
    originCity: '',
    originState: '',
    originPostal: '',
    originCountry: 'US',
    destinationLane: 'Denver, Colorado US 80202',
    destinationCity: '',
    destinationState: '',
    destinationPostal: '',
    destinationCountry: '',
    originLatitude: 0,
    originLongitude: 0,
    destinationLatitude: 0,
    destinationLongitude: 0,
    carrierId: 0,
    id: 3,
    equipmentCategory: 8,
    source: DataSource.Highway,
    canDelete: false,
    createdDate: '',
    createdBy: 0,
    modifiedDate: '',
    modifiedBy: 0,
    historyContext: '',
  },
];

/**
 * Get the list of preferred lanes for a given carrier id. You can also mock this with
 * the data in `dummyPreferredLaneData`.
 * @description
 * `GET`: `/api/PreferredLane/carrier/{id}`
 * @returns
 */
export const getPreferredLanes = async (
  carrierId: number,
  dummyData = false,
): Promise<PreferredLaneT[]> => {
  if (dummyData) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(dummyPreferredLaneData);
      }, 150);
    });
  }

  try {
    const response: ApiResponse<PreferredLaneT[]> = await apiClient.get(
      `PreferredLane/carrier/${carrierId}`,
    );

    if (!response.isSuccess) {
      throw response.error;
    }

    return response.value;
  } catch (e: unknown) {
    throw createError(
      e,
      `Unable to get preferred lanes for carrier ${carrierId}`,
    );
  }
};

/**
 * Get a single lane by its id.
 * @description
 * `GET`: `/api/PreferredLane/lane/{id}`
 * @returns
 */
export const getLane = async (id: number) => {
  try {
    const response: ApiResponse<PreferredLaneT> = await apiClient.get(
      `PreferredLane/lane/${id}`,
    );

    if (!response.isSuccess) {
      throw response.error;
    }

    return response.value;
  } catch (e: unknown) {
    throw createError(e, 'Unable to get preferred lane');
  }
};

/**
 * Get the list of accepted lane equipment categories.
 * @description
 * `GET`: `/api/PreferredLane/categories`
 * @returns
 */
export const getLaneEquipmentCategories = async () => {
  try {
    const response: ApiResponse<EquipmentCategoryT[]> = await apiClient.get(
      'PreferredLane/categories',
    );

    if (!response.isSuccess) {
      throw response.error;
    }

    return response.value;
  } catch (e: unknown) {
    throw createError(e, 'Unable to get the list of categories');
  }
};

/**
 * Delete a lane by its id.
 * @description
 * `DELETE`: `/api/PreferredLane/lane/{id}`
 * @returns
 */
export const deleteLane = async (
  id: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: ApiResponse<never> = await apiClient.delete(
      `PreferredLane/lane/${id}`,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );

    if (!response.isSuccess) {
      throw response.error;
    }

    return true;
  } catch (e: unknown) {
    throw createError(e, 'Unable to delete preferred lane');
  }
};

/**
 * Creates a new lane from the data in `newLane`.
 * @description
 * `POST``: `/api/PreferredLane`
 * @returns
 */
export const createLane = async (
  newLane: Partial<PreferredLaneT>,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: ApiResponse<PreferredLaneT> = await apiClient.post(
      'PreferredLane',
      newLane,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );

    if (!response.isSuccess) {
      throw response.error;
    }

    return response.value;
  } catch (e: unknown) {
    throw createError(e, 'Unable to create preferred lane');
  }
};

/**
 * Updates a new lane from the data in `lane`.
 * @description
 * `PUT``: `/api/PreferredLane`
 * @returns
 */
export const updateLane = async (
  lane: Partial<PreferredLaneT>,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: ApiResponse<PreferredLaneT> = await apiClient.put(
      'PreferredLane',
      lane,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );

    if (!response.isSuccess) {
      throw response.error;
    }

    return response.value;
  } catch (e: unknown) {
    throw createError(e, 'Unable to update preferred lane');
  }
};

export const fetchLanesByGridSpecification = async (
  payload: Record<string, unknown>,
) => {
  try {
    const response: V4ApiResponse<LaneSearchGridApiResponse> =
      await apiClient.post('Carrier/LaneSearchGrid', payload);
    if (!response.isSuccess) {
      throw new Error(
        response.errors.map(({ message }) => message).join(', ') ||
          'Failed to get carrier grid data',
      );
    }
    return response;
  } catch (error) {
    logError('fetchLanesByGridSpecification', error as Error);
    throw createError(error, 'Failed to get carrier grid data');
  }
};

export const claimHoldRelease = async (claims: ClaimReleaseViewModel[]) => {
  try {
    const response: ApiResponse<ClaimReleaseViewModel[]> = await apiClient.put(
      'carrier/claimholdrelease',
      claims,
    );

    if (!response.isSuccess) {
      throw new Error('Hold was not released');
    }

    return response.value;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to release claim hold');
    logError('claimHoldRelease', error);
    throw error;
  }
};

export const isLoadAssociatedWithCarrier = async (
  carrierId: number,
  orderId: number,
  orderSequence: number,
) => {
  try {
    const response: ApiResponse<boolean> = await apiClient.get(
      `carrier/${carrierId}/order/${orderId}/sequence/${orderSequence}`,
    );

    if (!response.isSuccess) {
      throw new Error('Failed to check if load is associated with carrier');
    }

    return response.value;
  } catch (e: unknown) {
    const error = createError(
      e,
      'Failed to check if load is associated with carrier',
    );
    logError('isLoadAssociatedWithCarrier', error);
    throw error;
  }
};

export const getClaimableLoads = async (
  carrierId: number,
  orderId: number,
  orderSequence: number,
) => {
  try {
    const response: ApiResponse<CarrierPaymentHoldsViewModelT> =
      await apiClient.get(
        `carrier/claimableloads/${carrierId}/${orderId}/${orderSequence}`,
      );

    if (!response.isSuccess) {
      throw new Error(
        response.errors?.map(({ message }) => message).join(', ') ??
          response.error?.message ??
          'Unknown error occurred while fetching loads associated with carrier',
      );
    }

    return response.value;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get loads associated with carrier');
    logError('getClaimableLoads', error);
    throw error;
  }
};

export const createCarrierClaim = async (
  claim: CarrierPaymentHoldsViewModelT,
) => {
  try {
    const response: ApiResponse<CarrierPaymentHoldsViewModelT> =
      await apiClient.post('carrier/claimloads', claim);

    if (!response.isSuccess) {
      throw new Error('Claim hold was not created');
    }

    return response.value;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to create claim hold');
    logError('createCarrierClaim', error);
    throw error;
  }
};
